import { useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, MessageCircle, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETING = {
  role: "assistant",
  text: "Hello — I can answer anything about the free pilot: terms, holding deposits, liability, what the lock does and doesn't do. What's on your mind?",
};

export const Concierge = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useMemo(() => {
    let id = localStorage.getItem("ss_concierge_session");
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem("ss_concierge_session", id);
    }
    return id;
  }, []);

  const scrollDown = () => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  };

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    scrollDown();
    try {
      const res = await fetch(`${API}/concierge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const chunk = JSON.parse(payload);
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + chunk };
              return copy;
            });
            scrollDown();
          } catch {
            /* partial chunk — wait for more */
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: "Sorry — I had trouble with that. Please try again in a moment.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
      scrollDown();
    }
  };

  return (
    <>
      <motion.button
        data-testid="concierge-toggle"
        onClick={() => setOpen((o) => !o)}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2.2, duration: 0.5 }}
        className="fixed bottom-6 right-6 z-50 flex h-14 items-center gap-2.5 rounded-full bg-accent px-6 font-semibold text-accent-foreground shadow-[0_8px_30px_rgba(37,99,235,0.45)] transition-colors hover:bg-blue-500"
        aria-label="Open pilot concierge chat"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
        <span className="hidden sm:inline">{open ? "Close" : "Ask about the pilot"}</span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="concierge-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-50 flex h-[480px] w-[calc(100vw-3rem)] max-w-sm flex-col overflow-hidden rounded-lg border border-border bg-white shadow-2xl"
          >
            <div className="border-b border-border bg-slate-950 px-5 py-4">
              <p className="font-serif text-base font-semibold tracking-tight text-white">
                Pilot concierge
              </p>
              <p className="text-[11px] text-slate-400">
                Answers powered by Gemini · straight answers, no sales pitch
              </p>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
              {messages.map((msg, i) => (
                <div
                  key={i}
                  data-testid={`concierge-message-${i}`}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-lg px-4 py-2.5 text-sm leading-relaxed ${
                      msg.role === "user"
                        ? "bg-accent text-accent-foreground"
                        : "bg-slate-100 text-foreground"
                    }`}
                  >
                    {msg.text ||
                      (msg.role === "assistant" && (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      ))}
                  </div>
                </div>
              ))}
            </div>

            <form onSubmit={send} className="flex items-center gap-2 border-t border-border p-3">
              <Input
                data-testid="concierge-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="e.g. Who pays for lost locks?"
                maxLength={500}
                className="h-10 rounded-full"
              />
              <Button
                data-testid="concierge-send-btn"
                type="submit"
                disabled={busy || !input.trim()}
                className="h-10 w-10 shrink-0 rounded-full bg-accent p-0 text-accent-foreground hover:bg-blue-700 transition-colors"
                aria-label="Send message"
              >
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
